import type { AxiosError, InternalAxiosRequestConfig } from 'axios'
import axios from 'axios'
import * as Sentry from '@sentry/vue'
import { Constants } from '../utils/enums'
import { createDiscreteApi } from 'naive-ui'
import { useAuthStore } from './authStore'
import { useUserStore } from './userStore'
import { useErrorAssistStore } from './errorAssistStore'
import { ApiErrorKind } from '@/types/api-error.type'
import { summarizePayload } from '@/utils/api-error-payload'
const { message } = createDiscreteApi(['message'])

// 画面の裏で走るリクエストが立てる。ユーザーが起こしていないリクエストの失敗を
// toast で騒がせず、エラー記録（相関材料）にも積まないため
declare module 'axios' {
  export interface AxiosRequestConfig {
    backgroundRequest?: boolean
  }
}

// Define Axios Response / Error Types
export type _AxiosError = AxiosError & {
  response: {
    data: { message: string }
  }
}
const loginUrl = '/auth/login'
const localLoginUrl = '/auth/local/login'
const refreshUrl = '/auth/refresh'
const authUrls = [loginUrl, localLoginUrl, refreshUrl]

// 各 store の .catch は固定文言を出すだけで statusCode / path / 時刻を落とすため、
// 全 API 呼び出しの単一通過点である interceptor で拾う
function recordApiError(err: AxiosError) {
  const response = err.response
  const data = response?.data as { message?: unknown } | undefined
  const serverMessage = typeof data?.message === 'string' ? data.message : undefined

  // axios は config / url / method を防御的に optional 宣言しているが、response interceptor
  // に届く時点で送信済みのリクエストなので実体はある。境界のここだけで narrow する
  const config = err.config as InternalAxiosRequestConfig & { url: string; method: string }
  if (config.backgroundRequest) return
  const url = config.url
  const method = config.method.toUpperCase()
  // 通常の 401 は下の interceptor が refresh してリクエストを再送するので失敗ではない。
  // 一方 refresh / login 自体の 401 は回復せずログアウト・ログイン失敗になるため記録する
  const isTransparentlyRetried401 = response?.status === 401 && !authUrls.includes(url)
  if (isTransparentlyRetried401) return

  const kind = (() => {
    if (!response) return ApiErrorKind.network
    if (response.status >= 500) return ApiErrorKind.server
    return ApiErrorKind.client
  })()

  useErrorAssistStore().recordError({
    kind,
    statusCode: response?.status,
    method,
    path: url,
    serverMessage,
    details: {
      requestQuery: summarizePayload(config.params),
      // 認証系の body は資格情報そのもの。キー名での伏せ字に頼らず丸ごと載せない
      requestBody: url.startsWith('/auth/') ? undefined : summarizePayload(config.data),
      responseBody: summarizePayload(response?.data),
    },
    observedAt: new Date().toISOString(),
    route: location.hash || location.pathname,
  })
}

const axiosSalesAi = axios.create({
  baseURL: Constants['BASE_URL_API'],
  withCredentials: true,
  timeout: 300_000, // 5分
})
// 並行する 401 で refresh が多重発火しないよう single-flight 化する
let refreshInFlight: Promise<unknown> | null = null
axiosSalesAi.interceptors.response.use(
  (res) => {
    return res
  },
  (err) => {
    recordApiError(err)
    if (err.response?.status == 401) {
      if (err.config.url == refreshUrl) {
        // refresh自体が401 → セッション切れなのでログアウト
        Sentry.captureMessage('Auth: refresh failed with 401, logging out', {
          level: 'warning',
          extra: {
            originalUrl: err.config._originalUrl,
            responseStatus: err.response?.status,
          },
        })
        // トークンは失効済みなので、cookie を消せたかに関わらず画面上はログアウトさせる
        useUserStore().clearCurrentUser()
        useAuthStore()
          .logout()
          .catch(() => {})
        setTimeout(() => {
          message.error('Please log in again')
          return (location.href = '/#/login?authError=true')
        }, 100)
      } else if (err.config.url !== loginUrl && err.config.url !== localLoginUrl) {
        Sentry.addBreadcrumb({
          category: 'auth',
          message: `401 received from ${err.config.url}, attempting refresh`,
          level: 'info',
        })
        refreshInFlight ??= axiosSalesAi.post(refreshUrl).finally(() => {
          refreshInFlight = null
        })
        return refreshInFlight.then(() => axiosSalesAi.request(err.config))
      }
    } else if (err.code === 'ECONNABORTED') {
      if (!err.config.backgroundRequest) message.error('Request timed out')
    } else if (!err?.response) {
      if (!err.config.backgroundRequest) message.error('A network error occurred')
    }
    // NavigationGuardでauthedHelloしてるケース
    return Promise.reject(err)
  },
)
export default axiosSalesAi
